<template>
  <v-dialog v-model="dialog" max-width="1000px" width="95vw" persistent content-class="staff-holiday-dialog">
    <v-card>
      <v-card-title class="text-h6 grey lighten-3">
        <v-icon class="mr-2">mdi-calendar-remove</v-icon>
        {{ $getUserDisplayName(staff.name) }}님 휴일 관리
      </v-card-title>

      <v-card-text class="pt-4 staff-holiday-dialog__content pb-6">
        <v-row>
          <!-- 휴일 등록 섹션 -->
          <v-col cols="12" md="6">
            <div class="mb-2 font-weight-bold">휴일 등록</div>
            <v-date-picker v-model="newHolidayDate" full-width locale="ko" :min="minDate" color="primary" class="mb-3"></v-date-picker>

            <v-textarea v-model="newHolidayComment" label="휴무 사유 (선택사항)" rows="3" outlined dense hide-details class="mb-2 staff-holiday-dialog__comment"></v-textarea>

            <v-btn color="primary" block @click="addHoliday" :loading="adding" :disabled="!newHolidayDate">
              <v-icon left small>mdi-plus</v-icon>
              휴일 등록
            </v-btn>
          </v-col>

          <!-- 등록된 휴일 목록 -->
          <v-col cols="12" md="6">
            <div class="mb-2 font-weight-bold">등록된 휴일 목록</div>

            <v-alert v-if="loading" text type="info" dense class="ma-2">
              <v-progress-circular indeterminate size="20" class="mr-2"></v-progress-circular>
              불러오는 중...
            </v-alert>

            <v-alert v-else-if="error" text type="error" dense dismissible @input="error = null" class="ma-2">
              {{ error }}
            </v-alert>

            <div v-else-if="holidays.length === 0" class="text-center grey--text pa-4">등록된 휴일이 없습니다.</div>

            <v-list v-else dense class="holiday-list">
              <v-list-item v-for="holiday in sortedHolidays" :key="holiday.id" class="holiday-item">
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    {{ formatDate(holiday.holidayDate) }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="holiday.comment" class="text-caption">
                    {{ holiday.comment }}
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action>
                  <v-btn icon small color="error" @click="confirmDelete(holiday)" :disabled="deleting === holiday.id">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">닫기</v-btn>
      </v-card-actions>
    </v-card>

    <!-- 삭제 확인 다이얼로그 -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">휴일 삭제 확인</v-card-title>
        <v-card-text>
          <div v-if="holidayToDelete">
            <div class="mb-2">다음 휴일을 삭제하시겠습니까?</div>
            <div class="font-weight-bold primary--text">
              {{ formatDate(holidayToDelete.holidayDate) }}
            </div>
            <div v-if="holidayToDelete.comment" class="text-caption grey--text mt-1">
              {{ holidayToDelete.comment }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">취소</v-btn>
          <v-btn color="error" text @click="deleteHoliday" :loading="!!deleting">삭제</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import dayjs from "dayjs"

export default {
  name: "StaffHolidayDialog",

  props: {
    value: Boolean,
    staff: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      holidays: [],
      loading: false,
      adding: false,
      deleting: null,
      error: null,

      newHolidayDate: null,
      newHolidayComment: "",

      deleteDialog: false,
      holidayToDelete: null,

      minDate: dayjs().format("YYYY-MM-DD"),
    }
  },

  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit("input", val)
      },
    },

    sortedHolidays() {
      return [...this.holidays].sort((a, b) => {
        return dayjs(a.holidayDate).valueOf() - dayjs(b.holidayDate).valueOf()
      })
    },
  },

  watch: {
    dialog(val) {
      if (val) {
        this.loadHolidays()
        this.resetForm()
      }
    },
  },

  methods: {
    async loadHolidays() {
      this.loading = true
      this.error = null

      try {
        const response = await this.$http.get(`/api/admin/staff/${this.staff.id}/holidays`)

        if (response.data) {
          this.holidays = response.data.holidays || []
        }
      } catch (error) {
        console.error("휴일 목록 조회 실패:", error)
        this.error = "휴일 목록을 불러오는데 실패했습니다."
      } finally {
        this.loading = false
      }
    },

    async addHoliday() {
      if (!this.newHolidayDate) {
        this.$toast.error("날짜를 선택해주세요.")
        return
      }

      this.adding = true
      this.error = null

      try {
        const response = await this.$http.post(`/api/admin/staff/${this.staff.id}/holidays`, {
          holidayDate: this.newHolidayDate,
          comment: this.newHolidayComment || null,
        })

        // API는 성공 시 inserted[0] 객체를 직접 반환 (id, staffUserId, holidayDate, comment, createdAt)
        if (response.data && response.data.id) {
          this.holidays.push(response.data)
          this.error = null
          this.resetForm()
        } else {
          throw new Error("응답 데이터 형식이 올바르지 않습니다.")
        }
      } catch (error) {
        console.error("휴일 등록 실패:", error)
        if (error.response?.status === 409) {
          this.error = "이미 등록된 휴무일입니다."
        } else {
          this.error = error.response?.data?.message || "휴일 등록에 실패했습니다."
        }
      } finally {
        this.adding = false
      }
    },

    confirmDelete(holiday) {
      this.holidayToDelete = holiday
      this.deleteDialog = true
    },

    async deleteHoliday() {
      if (!this.holidayToDelete) return

      const idToDelete = this.holidayToDelete.id
      this.deleting = idToDelete

      try {
        const response = await this.$http.delete(`/api/admin/staff/${this.staff.id}/holidays/${this.holidayToDelete.id}`)

        // API는 성공 시 { success: true, message: "..." } 반환
        if (response) {
          this.holidays = this.holidays.filter((h) => h.id !== idToDelete)
          this.error = null
          this.deleteDialog = false
          this.holidayToDelete = null
        } else {
          console.warn("삭제 응답 형식:", response)
          throw new Error("응답 데이터 형식이 올바르지 않습니다.")
        }
      } catch (error) {
        console.error("휴일 삭제 실패:", error)
        console.error("에러 응답:", error.response)
        if (error.response?.status === 404) {
          // 이미 삭제된 경우 목록에서만 제거
          this.holidays = this.holidays.filter((h) => h.id !== idToDelete)
          this.error = error.response?.data?.message || "휴무일을 찾을 수 없습니다."
          this.deleteDialog = false
          this.holidayToDelete = null
        } else {
          this.error = error.response?.data?.message || "휴일 삭제에 실패했습니다."
        }
      } finally {
        this.deleting = null
      }
    },

    formatDate(dateStr) {
      return dayjs(dateStr).format("YYYY년 MM월 DD일 (ddd)")
    },

    resetForm() {
      this.newHolidayDate = null
      this.newHolidayComment = ""
      this.error = null
    },

    close() {
      this.dialog = false
      this.$emit("close")
    },
  },
}
</script>

<style scoped>
.staff-holiday-dialog__content {
  overflow: visible;
  min-width: 0; /* flex 자식이 부모보다 넓어져 가로 스크롤 방지 */
}

.staff-holiday-dialog__comment {
  min-height: 0;
}

.holiday-list {
  max-height: 400px;
  overflow-y: auto;
  min-height: 0;
}

.holiday-item {
  border-bottom: 1px solid #e0e0e0;
}

.holiday-item:last-child {
  border-bottom: none;
}
</style>

<style>
/* 다이얼로그 래퍼: 불필요한 스크롤 방지 (scoped 아님) */
.staff-holiday-dialog .v-card {
  overflow: visible;
}

.staff-holiday-dialog .v-card__text {
  overflow: visible;
}
</style>
